import config from '../config/environment';

export interface AppErrorDetails {
  action?: string;
  error?: unknown;
  context?: Record<string, unknown>;
}

interface AuthSnapshot {
  userEmail?: string;
  userName?: string;
  tenantId?: string | number;
  tenantName?: string;
}

function getAuthSnapshot(): AuthSnapshot {
  try {
    const auth = JSON.parse(localStorage.getItem('metrica_auth') || 'null');
    const tenant = JSON.parse(localStorage.getItem('metrica_tenant') || 'null');
    return {
      userEmail: auth?.user?.email,
      userName: auth?.user?.name,
      tenantId: tenant?.id ?? auth?.tenant?.id,
      tenantName: tenant?.name ?? auth?.tenant?.name,
    };
  } catch {
    return {};
  }
}

export function serializeError(error: unknown): string {
  if (error instanceof Error) {
    return [error.message, error.stack].filter(Boolean).join('\n');
  }
  if (typeof error === 'string') {
    return error;
  }
  if (error && typeof error === 'object') {
    try {
      return JSON.stringify(error, null, 2);
    } catch {
      return String(error);
    }
  }
  if (error == null) {
    return '';
  }
  return String(error);
}

export function buildErrorReport(summary: string, details?: AppErrorDetails): string {
  const auth = getAuthSnapshot();
  const technicalError = serializeError(details?.error);
  const contextJson = details?.context
    ? JSON.stringify(details.context, null, 2)
    : '';

  const lines = [
    '=== Metrica Expenses — Error Report ===',
    '',
    `Generated (UTC): ${new Date().toISOString()}`,
    `Environment: ${config.app.environment}`,
    `App version: ${config.app.version}`,
    `API base URL: ${config.api.baseUrl}`,
    '',
    '--- Location ---',
    `Page URL: ${typeof window !== 'undefined' ? window.location.href : 'N/A'}`,
    `Page path: ${typeof window !== 'undefined' ? window.location.pathname : 'N/A'}`,
    '',
    '--- User summary ---',
    summary,
  ];

  if (details?.action) {
    lines.push('', '--- Action ---', details.action);
  }

  if (technicalError) {
    lines.push('', '--- Error ---', technicalError);
  }

  if (contextJson) {
    lines.push('', '--- Context ---', contextJson);
  }

  lines.push(
    '',
    '--- User / tenant ---',
    `User: ${auth.userName || 'Unknown'}`,
    `Tenant: ${auth.tenantName || 'Unknown'} (id: ${auth.tenantId ?? 'Unknown'})`,
    `Send to: info@metrica.co.il`,
    '',
    '--- Client ---',
    `Browser: ${typeof navigator !== 'undefined' ? navigator.userAgent : 'N/A'}`,
    `Language: ${typeof navigator !== 'undefined' ? navigator.language : 'N/A'}`,
    '',
    '=== End of report ===',
  );

  return lines.join('\n');
}

type ErrorReporter = (summary: string, details?: AppErrorDetails) => void;

let reporter: ErrorReporter | null = null;
let lastReportKey = '';
let lastReportAt = 0;

export function registerErrorReporter(next: ErrorReporter | null) {
  reporter = next;
}

/** Show the copyable error window from any layer, including outside React hooks. */
export function reportAppError(summary: string, details?: AppErrorDetails) {
  const key = `${summary}|${serializeError(details?.error)}|${details?.action || ''}`;
  const now = Date.now();
  if (key === lastReportKey && now - lastReportAt < 1500) {
    return;
  }
  lastReportKey = key;
  lastReportAt = now;

  if (reporter) {
    reporter(summary, details);
    return;
  }
  console.error(summary, details?.error ?? details);
}
